import { type MouseEventHandler, memo, type ReactNode } from 'react';

import { BoardIcon } from '@air/design-icons';

import { toWorkspaceAgentPreviewCardPlaceholderProps } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/toWorkspaceAgentPreviewCardPlaceholderProps';
import {
  WorkspaceAgentPreviewCardFrame,
  type WorkspaceAgentPreviewCardFrameProps,
} from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardFrame';
import { WorkspaceAgentPreviewCardMetaRow } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentPreviewCardMetaRow';
import { type WorkspaceAgentBoardLocation } from '~/components/v2/components/workspace/workspace-agent-panel/utils/workspaceAgentLocationLabels';

type WorkspaceAgentBoardPreviewCardFrameInput = {
  coverImageUrl: string | null;
  itemCount: number | null;
  location: WorkspaceAgentBoardLocation | null;
  onBodyMouseDown?: MouseEventHandler<HTMLButtonElement>;
  onOpen: () => void;
  title: string;
};

const toBoardFrameProps = ({
  coverImageUrl,
  itemCount,
  location,
  onBodyMouseDown,
  onOpen,
  title,
}: WorkspaceAgentBoardPreviewCardFrameInput): WorkspaceAgentPreviewCardFrameProps => ({
  children:
    itemCount === null ? null : (
      // No glyph: the prototype draws this line bare, and the meta row owns the 11px type.
      <WorkspaceAgentPreviewCardMetaRow>
        {itemCount === 1 ? '1 item' : `${itemCount} items`}
      </WorkspaceAgentPreviewCardMetaRow>
    ),
  locationLabel: location?.label ?? null,
  locationTitle: location?.fullTrail ?? null,
  // Prototype drew this `gray-8`; bumped to `gray-9` for 3:1 against the band's gray-3 ground.
  mediaFallback: <BoardIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size={32} />,
  mediaImageUrl: coverImageUrl,
  onBodyMouseDown,
  onOpen,
  openTestId: 'WORKSPACE_AGENT_BOARD_PREVIEW_OPEN',
  testId: 'WORKSPACE_AGENT_BOARD_PREVIEW_CARD',
  title,
});

export type WorkspaceAgentBoardPreviewCardProps = {
  /** Passed in so the card stays free of popover context. */
  closeButton: ReactNode;
  coverImageUrl: string | null;
  /** Still in flight, as opposed to settled and missing. */
  isLoading: boolean;
  /** `null` while still resolving. */
  itemCount: number | null;
  /** A deep trail draws middle-collapsed, with every crumb kept for the row's tooltip. */
  location: WorkspaceAgentBoardLocation | null;
  /** A composer host guards the press so the editor keeps focus and selection. */
  onBodyMouseDown?: MouseEventHandler<HTMLButtonElement>;
  onOpen: () => void;
  /** Shown on hover of the unavailable placeholder, for reporting a bad reference. */
  referenceId: string;
  /** `null` until the board resolves, which is also what the missing state reads as. */
  title: string | null;
};

/** The board half of the preview card API: a host hands it a subject, and it draws every state of it. */
export const WorkspaceAgentBoardPreviewCard = memo(
  ({
    closeButton,
    coverImageUrl,
    isLoading,
    itemCount,
    location,
    onBodyMouseDown,
    onOpen,
    referenceId,
    title,
  }: WorkspaceAgentBoardPreviewCardProps) => {
    // Resolving and settled-and-missing are both card bodies, so only the detail rows change height.
    let frameProps: WorkspaceAgentPreviewCardFrameProps = toWorkspaceAgentPreviewCardPlaceholderProps({
      label: 'Loading board',
    });

    if (title !== null) {
      frameProps = toBoardFrameProps({ coverImageUrl, itemCount, location, onBodyMouseDown, onOpen, title });
    } else if (!isLoading) {
      frameProps = toWorkspaceAgentPreviewCardPlaceholderProps({
        isUnavailable: true,
        label: 'Unavailable board',
        referenceId,
      });
    }

    // One frame across all three states: a swap would unmount the close button Radix autofocused on open.
    return <WorkspaceAgentPreviewCardFrame {...frameProps} closeButton={closeButton} />;
  },
);

WorkspaceAgentBoardPreviewCard.displayName = 'WorkspaceAgentBoardPreviewCard';
